import firebase from "../FirebaseConfig.js";

export const FIREBASE_NETWORK_ERROR = "auth/network-request-failed";

const IDB_SETTLE_MS = 400;

export function isFirebaseNetworkError(error) {
  return error?.code === FIREBASE_NETWORK_ERROR;
}

export function isFirebaseIndexedDbError(error) {
  const message = String(error?.message || "");
  return (
    message.includes("database connection is closing") ||
    message.includes("IDBDatabase")
  );
}

export function isFirebaseRecoverableAuthError(error) {
  return isFirebaseNetworkError(error) || isFirebaseIndexedDbError(error);
}

function waitForIdbSettle() {
  return new Promise((resolve) => setTimeout(resolve, IDB_SETTLE_MS));
}

/**
 * Clears persisted Firebase auth state (localStorage + IndexedDB).
 * Used only after a recoverable persistence failure to mimic a clean session.
 */
export async function clearFirebaseAuthPersistence() {
  try {
    await firebase.auth().signOut();
  } catch (_) {
    // ignore — session may already be broken
  }

  try {
    Object.keys(window.localStorage).forEach((key) => {
      if (key.startsWith("firebase:") || key.startsWith("firebaseui::")) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (_) {
    // ignore
  }

  try {
    if (window.indexedDB?.deleteDatabase) {
      await new Promise((resolve) => {
        const request = window.indexedDB.deleteDatabase("firebaseLocalStorageDb");
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
        request.onblocked = () => setTimeout(resolve, IDB_SETTLE_MS);
      });
    }
  } catch (_) {
    // ignore
  }

  await waitForIdbSettle();
}

/**
 * Email/password sign-in with a single retry when IndexedDB is closing
 * or auth/network-request-failed left a stale Firebase persistence DB.
 */
export async function signInWithEmailAndPasswordWithNetworkRetry(email, password) {
  try {
    return await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (error) {
    if (!isFirebaseRecoverableAuthError(error)) {
      throw error;
    }
    if (isFirebaseIndexedDbError(error)) {
      await waitForIdbSettle();
    } else {
      await clearFirebaseAuthPersistence();
    }
    return firebase.auth().signInWithEmailAndPassword(email, password);
  }
}
